/* eslint-disable @typescript-eslint/no-this-alias */
import s3 from '../../s3';

define((require, exports, module) => {
    'use strict';

    const uploadLog = require('./uploadLog');
    const uploadRadar = require('./uploadRadar');
    const fileSystemEntryReader = require('./tools/fs/fileSystemEntryReader');
    const fileSystemPath = require('./tools/fs/fileSystemPath');
    const Model = require('Model');
    const Uploader = require('./tools/s3/Uploader');
    const UploaderData = require('./tools/s3/UploaderData');
    const UploadReason = require('./UploadReason');
    const UploadCancel = require('./UploadCancel');
    const JavaScriptError = require('./errors/JavaScriptError');
    const UnsupportedFolderTransferFail = require('./fails/UnsupportedFolderTransferFail');

    const EMPTY_FILE = new Blob([]);

    const STATE_PENDING = 'pending';
    const STATE_UPLOADING = 'uploading';
    const STATE_UPLOADED = 'uploaded';
    const STATE_DONE = 'done';
    const STATE_FAIL = 'fail';
    const STATE_CANCEL = 'cancel';

    /**
     * @constructor
     */
    const UploadDescriptor = Model.extend({
        /**
         * Название класса
         * @type {string}
         */
        className: 'UploadDescriptor',

        /**
         * Свойства по умолчанию
         * @type {Object}
         */
        defaults: {
            /**
             * Уникальный идентификатор.
             * @type {string}
             */
            id: '',

            /**
             * Имя файла или папки (в Unicode NFC) в локальной ФС.
             * @type {string}
             */
            localName: '',

            /**
             * Путь к файлу или папке (в Unicode NFC) в локальной ФС.
             * @type {string}
             */
            localPath: '',

            /**
             * Имя файла или папки (в Unicode NFC) в Объектном хранилище.
             * @type {string}
             */
            remoteName: '',

            /**
             * Путь к файлу или папке (в Unicode NFC) в Объектном хранилище.
             * @type {string}
             */
            remotePath: '',

            /**
             * Имя файла или папки для отображения.
             * Пример: в macOS "file:name.ext" отображается, как "file/name.ext"
             * @type {string}
             */
            visibleName: '',

            /**
             * Путь к файлу или папке для отображения.
             * @type {string}
             */
            visiblePath: '',

            /**
             * Имя и расширение в формате для b-filename.
             * @type {Object}
             */
            nameParts: {
                /**
                 * Имя файла (без расширения) или папки.
                 * @type {string}
                 */
                name: '',

                /**
                 * Расширение файла в исходном регистре.
                 * @type {string}
                 */
                extension: '',
            },

            /**
             * Размер файла (в байтах).
             * @type {number}
             */
            size: -1,

            /**
             * Что-то вроде дескриптора файла или папки в локальной ФС,
             * содержат информацию о имени и относительном пути.
             * Служит для получения инстанса File.
             * @type {FileEntry|DirectoryEntry|null}
             */
            entry: null,

            /**
             * Соответствует файлу в локальной ФС.
             * Служит для получения содержимого файла.
             * @type {File}
             */
            file: null,

            /**
             * Любая ошибка.
             * @type {Error}
             */
            error: null,

            /**
             * @type {boolean}
             */
            isFile: false,

            /**
             * @type {boolean}
             */
            isDirectory: false,

            /**
             * Используется для рекурсивного чтения DirectoryEntry.
             * @type {boolean}
             */
            isUnreadDirectory: false,

            /**
             * Родительский каталог.
             * @type {UploadDescriptor|null}
             */
            parent: null,

            /**
             * Коллекция дескрипторов дочерних файлов и папок.
             * @type {Array}
             */
            children: [], // TODO: UploadDescriptor.List

            /**
             * @type {Uploader}
             */
            uploader: null,

            /**
             * Прогресс загрузки (в процентах).
             * @type {number}
             */
            progress: 0,

            /**
             * Состояние загрузки файла.
             * @type {number}
             */
            state: STATE_PENDING,

            /**
             * @type {Radar}
             */
            radar: null,

            /**
             * The canned ACL to apply to the object.
             * @type {string}
             */
            ACL: '',
        },

        /**
         * @param {any} value
         * @returns {any} – new value
         */
        _validateError(value) {
            const descriptor = this;
            let error = value;

            if (error) {
                if (!(error instanceof UploadReason)) {
                    const source = UploadReason.SOURCE_WEB_CLIENT;
                    const stack = new Error('JavaScriptError');

                    error = new JavaScriptError(stack, source, error);
                }

                error.id = descriptor.get('id');
            }

            return error;
        },

        _sendCounter() {
            const descriptor = this;
            const radar = descriptor.get('radar');

            if (!radar.alreadySended) {
                radar.send();
            }
        },

        /**
         * Используется для отправки счетчиков, если descriptor получил
         * конечное состояние до начала аплоада.
         * Ориентироваться на изменение атрибута state нельзя,
         * т.к. не известно, в каком порядке обновляются значения атрибутов.
         */
        finish() {
            this._sendCounter();
        },

        _logError(reason) {
            const descriptor = this;
            let radarName = reason.radarName;

            if (radarName) {
                const radar = descriptor.get('radar');

                // Дублируем причину ошибки рядом с состояниями,
                // размером файла и количеством попыток.
                radarName = radarName.replace(/^(fail|error|cancel)_/, '');
                radarName = radarName.replace(/_/g, '-');

                radar.add('reason-' + radarName, 0);
            }

            uploadLog.reason(reason);
        },

        _logState(state) {
            const descriptor = this;
            const radar = descriptor.get('radar');

            radar.timeEnd('state-' + state);
            uploadLog.info({
                id: descriptor.get('id'),
                state,
            });
        },

        _logSize(size) {
            const descriptor = this;
            const radar = descriptor.get('radar');

            radar.add('size', size);
            uploadLog.info({
                id: descriptor.get('id'),
                size,
            });
        },

        /**
         * @param {any} value
         * @param {string} key
         * @param {Object} attributes
         * @param {UploadDescriptor} descriptor
         * @returns {any} – new value
         */
        valueValidator(value, key, attributes, descriptor) {
            let newValue = value;

            switch (key) {
                case 'error':
                    newValue = descriptor._validateError(value);
                    descriptor._logError(newValue);

                    break;

                case 'state':
                    descriptor._logState(newValue);

                    break;

                case 'size':
                    descriptor._logSize(newValue);

                    break;
            }

            return newValue;
        },

        /**
         * @param {UploaderData} data
         */
        _onRetry(data) {
            const descriptor = this;

            descriptor.set('progress', 0);

            const radar = descriptor.get('radar');
            let reason = data.error;

            if (reason) {
                reason = descriptor._validateError(reason);

                const radarName = reason.radarName.replace(
                    /^(fail|error)_/,
                    'retry_',
                );

                uploadRadar.sendCounter(radarName);

                reason = Object.create(reason); // TODO: new UploadRetry
                reason.className += 'Retry';
                reason.radarName = ''; // prevent radar sent
                uploadLog.reason(reason);
            }

            radar.add('retry-' + data.attempt, 1);
        },

        /**
         * @param {UploaderData} data
         */
        _onProgress(data) {
            const descriptor = this;

            descriptor.set('progress', data.progress);
        },

        /**
         * @param {MicroEvent} event
         * @param {UploaderData} data
         */
        handleEvent(event, data) {
            switch (event.type) {
                case Uploader.EVENT_PROGRESS:
                    this._onProgress(data);

                    break;

                case Uploader.EVENT_RETRY:
                    this._onRetry(data);

                    break;
            }
        },

        /**
         * @returns {Promise}
         * @resolves {File}
         * @rejects {Error}
         */
        _getFile() {
            const descriptor = this;
            const isDirectory = descriptor.get('isDirectory');
            let promise;

            if (isDirectory) {
                promise = Promise.resolve(EMPTY_FILE);
            } else {
                const file = descriptor.get('file');

                if (file) {
                    promise = Promise.resolve(file);
                } else {
                    const entry = descriptor.get('entry');

                    promise = fileSystemEntryReader.getFile(entry);

                    promise = promise.then((file) => {
                        descriptor.set({
                            file,
                            size: file.size,
                        });

                        return file;
                    });

                    promise = promise.catch((reason) => {
                        descriptor.set({
                            error: reason,
                            state: STATE_FAIL,
                        });
                        descriptor._sendCounter();

                        return Promise.reject(reason);
                    });
                }
            }

            return promise;
        },

        /**
         * @returns {Promise}
         * @resolves {UploaderData}
         * @rejects {UploadReason|Error}
         */
        _upload() {
            const descriptor = this;
            let promise = descriptor._getFile();

            // Начинает загрузку.
            promise = promise.then((file) => {
                const path = descriptor.get('remotePath');
                const ACL = descriptor.get('ACL');
                const url = encodeURI(
                    fileSystemPath.join(s3.getPrivateEndpointUrl(), path),
                );
                const uploader = new Uploader({ file, url, ACL });

                uploader.on(Uploader.EVENT_PROGRESS, descriptor);
                uploader.on(Uploader.EVENT_RETRY, descriptor);

                descriptor.set('uploader', uploader);

                return uploader.upload();
            });

            // Файл успешно загружен.
            promise = promise.then((data) => {
                const uploader = descriptor.get('uploader');

                uploader.off(Uploader.EVENT_PROGRESS, descriptor);
                uploader.off(Uploader.EVENT_RETRY, descriptor);

                descriptor.set({
                    uploader: null,
                    progress: 100,
                    size: data.size,
                    state: STATE_UPLOADED,
                });

                uploadLog.info({
                    id: descriptor.get('id'),
                    size: data.size,
                    speed: data.speed,
                    attempt: data.attempt,
                });

                const radar = descriptor.get('radar');

                radar.add('speed', Math.round(data.speed));
                radar.add('attempt', data.attempt);

                return data;
            });

            // Файл не загружен.
            promise = promise.catch((reason) => {
                const uploader = descriptor.get('uploader');

                if (uploader) {
                    uploader.off(Uploader.EVENT_PROGRESS, descriptor);
                }

                let error;

                if (reason instanceof UploaderData) {
                    error = reason.error;
                } else {
                    error = reason;
                }

                let state;

                if (error instanceof UploadCancel) {
                    state = STATE_CANCEL;
                } else {
                    state = STATE_FAIL;
                }

                const attributes = {
                    uploader: null,
                    progress: 0,
                    error,
                    state,
                };

                if (error instanceof UnsupportedFolderTransferFail) {
                    // Safari для папок создает инстанс File,
                    // но его никак нельзя использовать.
                    attributes.isFile = false;
                    attributes.isDirectory = true;
                    attributes.isUnreadDirectory = false;
                }

                descriptor.set(attributes);
                descriptor._sendCounter();

                return Promise.reject(error);
            });

            return promise;
        },

        /**
         * @returns {Promise}
         * @resolves {UploaderData}
         * @rejects {UploadReason|Error}
         */
        upload() {
            const descriptor = this;
            const state = descriptor.get('state');
            let promise;

            if (state === STATE_PENDING) {
                descriptor.set('state', STATE_UPLOADING);
                promise = descriptor._upload();
            } else {
                promise = Promise.reject(
                    new Error('upload() called in wrong state'),
                );
            }

            promise = promise.then((data) => {
                descriptor.set({
                    state: STATE_DONE,
                });
                descriptor._sendCounter();

                return data;
            });

            promise = promise.catch((reason) => {
                if (!(reason instanceof UploadReason)) {
                    descriptor.set({
                        error: reason,
                        state: STATE_FAIL,
                    });
                }

                descriptor._sendCounter();

                return Promise.reject(reason);
            });

            return promise;
        },

        /**
         * @param {UploadCancel} reason
         */
        cancel(reason) {
            // TODO: cancel all descriptor.children
            const descriptor = this;
            const hasCancelableState = descriptor.hasCancelableState();

            if (hasCancelableState) {
                const uploader = descriptor.get('uploader');

                if (uploader) {
                    uploader.cancel();
                } else {
                    if (reason === undefined) {
                        const source = UploadReason.SOURCE_WEB_CLIENT;
                        const stack = new Error('UploadCancel');

                        reason = new UploadCancel(stack, source);
                    } else if (!(reason instanceof UploadCancel)) {
                        throw new TypeError('reason not cancel');
                    }

                    descriptor.set({
                        error: reason,
                        state: STATE_CANCEL,
                    });

                    if (!reason) {
                        // prevent this for cancel all
                        descriptor._sendCounter();
                    }
                }
            }
        },

        /**
         * @returns {boolean}
         */
        hasCancelableState() {
            const descriptor = this;
            const state = descriptor.get('state');
            return UploadDescriptor.isCancelableState(state);
        },

        /**
         * @returns {boolean}
         */
        hasFinishedState() {
            const descriptor = this;
            const state = descriptor.get('state');
            return UploadDescriptor.isFinishedState(state);
        },
    });

    Object.assign(UploadDescriptor, {
        version: '0.0.1',

        STATE_PENDING,
        STATE_UPLOADING,
        STATE_UPLOADED,
        STATE_DONE,
        STATE_FAIL,
        STATE_CANCEL,

        cancelableStates: [STATE_PENDING, STATE_UPLOADING, STATE_UPLOADED],

        finishedStates: [STATE_DONE, STATE_FAIL, STATE_CANCEL],

        /**
         * @param {string} state
         * @returns {boolean}
         */
        isCancelableState(state) {
            return UploadDescriptor.cancelableStates.includes(state);
        },

        /**
         * @param {string} state
         * @returns {boolean}
         */
        isFinishedState(state) {
            return UploadDescriptor.finishedStates.includes(state);
        },
    });

    module.exports = UploadDescriptor;
});
